import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp, LANG_SHORT } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { initials, avatarColor } from "@/lib/utils2";
import { Home, Wallet, Plus, Bell, Menu, Languages, Moon, Sun, HelpCircle } from "lucide-react";
import { useNotifications, NotificationBell, NotificationBanner } from "@/components/NotificationCenter";
import { HelpGuide } from "@/components/HelpGuide";
import InactivityGuard from "@/components/InactivityGuard";
import { WelcomeModal } from "@/components/WelcomeModal";
import { confirmLeaveIfBlocked } from "@/lib/navGuard";

export default function Layout({ children }) {
  const { t, cycleLang, lang, theme, toggleTheme } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const notif = useNotifications();
  const [helpOpen, setHelpOpen] = useState(false);
  // Bloque la navigation si une opération critique tourne (ex : analyse IA en cours).
  const guardNav = (e) => { if (!confirmLeaveIfBlocked()) e.preventDefault(); };

  // Affichage OBLIGATOIRE du guide juste après l'activation du compte.
  useEffect(() => {
    if (user && localStorage.getItem("pendingHelp") === "1") {
      localStorage.removeItem("pendingHelp");
      setHelpOpen(true);
    }
  }, [user]);

  const tabs = [
    { to: "/app", icon: Home, label: t("nav_home"), testid: "tab-home" },
    { to: "/finances", icon: Wallet, label: t("nav_finances"), testid: "tab-finances" },
    { to: "/action", icon: Plus, label: t("nav_action"), center: true, testid: "tab-action" },
    { to: "/announcements", icon: Bell, label: t("nav_announcements"), testid: "tab-announcements" },
    { to: "/more", icon: Menu, label: t("nav_more"), testid: "tab-more" },
  ];

  return (
    <div className="App min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-strong border-x-0 border-t-0">
        <div className="mx-auto max-w-3xl px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-2xl bg-gradient-to-br from-brand to-brand-dark flex items-center justify-center text-white font-black shrink-0 shadow-ios">
              A
            </div>
            <span className="font-bold text-base tracking-tight truncate">{t("appName")}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={cycleLang}
              data-testid="lang-toggle"
              className="h-10 px-3 rounded-full bg-secondary text-secondary-foreground font-semibold text-sm flex items-center gap-1.5 active:scale-95 transition-transform [&>*]:pointer-events-none"
            >
              <Languages className="h-4 w-4" />
              {LANG_SHORT[lang]}
            </button>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="h-10 w-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center active:scale-95 transition-transform"
            >
              {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </button>
            {user && (
              <button
                onClick={() => setHelpOpen(true)}
                data-testid="help-button"
                className="h-10 w-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center active:scale-95 transition-transform"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            )}
            {user && <NotificationBell notif={notif} />}
            {user && (
              <button
                onClick={() => { if (confirmLeaveIfBlocked()) navigate("/more"); }}
                data-testid="header-avatar"
                className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                style={{ backgroundColor: avatarColor(user.name) }}
              >
                {initials(user.name)}
              </button>
            )}
          </div>
        </div>
      </header>

      {user && <NotificationBanner notif={notif} />}
      {user && <HelpGuide open={helpOpen} onClose={() => setHelpOpen(false)} />}
      {user && <InactivityGuard />}
      {user && <WelcomeModal />}

      {/* Content */}
      <main className="mx-auto max-w-3xl px-4 pb-32 pt-4 animate-rise">{children}</main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-40 glass-strong border-x-0 border-b-0">
        <div className="mx-auto max-w-3xl h-20 px-2 flex items-center justify-around">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            if (tab.center) {
              return (
                <NavLink key={tab.to} to={tab.to} data-testid={tab.testid} onClick={guardNav}
                  className="flex flex-col items-center -mt-8">
                  <span className="h-16 w-16 rounded-full bg-gradient-to-br from-majorelle to-[#243b8f] text-white flex items-center justify-center shadow-[0_12px_30px_-6px_rgba(50,84,198,0.6)] ring-4 ring-majorelle/15 active:scale-90 hover:scale-[1.04] transition-transform duration-300">
                    <Icon className="h-8 w-8" />
                  </span>
                </NavLink>
              );
            }
            return (
              <NavLink key={tab.to} to={tab.to} data-testid={tab.testid} onClick={guardNav} className="flex flex-col items-center gap-1 w-16 py-1">
                {({ isActive }) => (
                  <>
                    <span className={`h-9 w-14 flex items-center justify-center rounded-2xl transition-all duration-300 ${isActive ? "bg-primary/12 text-brand dark:text-primary" : "text-muted-foreground"}`}>
                      <Icon className="h-[22px] w-[22px]" />
                    </span>
                    <span className={`text-[10px] font-semibold leading-none tracking-tight ${isActive ? "text-brand dark:text-primary" : "text-muted-foreground"}`}>{tab.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
